import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function TraderLabPrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="TraderLab"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how “TraderLab” (the “App”) collects, uses,
        stores, and discloses information. TraderLab is an educational mobile
        application for learning trading concepts through courses, video
        lessons, downloadable materials, and quizzes, with optional subscription
        access to premium content. The App runs on iOS and Android and is built
        with Flutter. Core services may use Firebase (Auth, Firestore, Storage,
        Remote Config, Analytics, Crashlytics, Messaging) and RevenueCat for
        in-app subscriptions. This Policy covers onboarding, content browsing,
        lesson playback, offline downloads, quiz participation, purchases and
        entitlements, notifications, analytics/crash diagnostics, and support
        communications. By using the App, you agree to the practices described.
        If you do not agree, uninstall the App and contact us regarding any data
        we control.
      </p>

      <div className={s.title}>2) Controller and Contact Information</div>
      <p>
        The controller that determines the purposes and means of processing for
        TraderLab is Aleksandra Rakitina. You can contact alexwadek@gmail.com
        for questions or to exercise rights such as access, deletion,
        correction, restriction, objection, or portability. To prevent
        unauthorized changes or disclosures, we verify identity before acting on
        requests, typically by confirming control of your account email or by
        requesting limited additional details. We retain privacy correspondence
        and logs of our responses as permitted by law to demonstrate compliance
        and for security auditing. Platform billing questions (refunds or
        cancellations) are handled in your device account settings; we will
        address privacy matters within our remit.
      </p>

      <div
        className={s.title}
      >{`3) Key Principles (Education-Only, Minimization, User Control)`}</div>
      <p>
        Education-only: the App offers instructional content, not financial
        advice, signals, or promises of returns. Minimization: we collect only
        what is necessary to deliver lessons, manage access, ensure reliability,
        and comply with platform requirements. User control: you choose whether
        to create an account, what content to view or download for offline use,
        whether to attempt quizzes, and whether to enable notifications. We
        avoid behavioral ad SDKs and do not sell Personal Data. Where features
        can work offline, we keep data local; where sync is needed (progress,
        purchases, push tokens), we scope it to the smallest practical set.
      </p>

      <div className={s.title}>4) Definitions</div>
      <p>
        “Personal Data” means information relating to an identified or
        identifiable person. “Content Data” covers courses, modules, lessons,
        captions, and downloadable materials you access. “Progress Data”
        includes lesson starts/completions, quiz attempts and scores, last
        position in a video, and download state. “Entitlement Data” refers to
        subscription status and eligibility from RevenueCat used to grant
        access. “Device Data” includes OS/app versions and anonymous
        identifiers needed for analytics and crash diagnostics. “Processing”
        includes collection, storage, transformation, transmission, and
        deletion. “Service Providers/Processors” are third parties acting on our
        instructions (Firebase, RevenueCat, CDN/hosting, notifications).
      </p>

      <div className={s.title}>5) What We Collect</div>
      <p>
        Depending on your choices and device settings, we may process: (a)
        Account data via Firebase Auth if you sign in (email, display name,
        provider IDs) or a local guest identifier if you use guest mode; (b)
        Progress Data (course/lesson IDs viewed, playhead position, lesson
        complete flags, quiz scores); (c) Download Data (which
        lessons/materials you saved for offline and storage usage); (d)
        Entitlement Data from RevenueCat (product IDs, trial eligibility,
        renewal results, expiration/cancellation) to unlock premium content; (e)
        Technical/analytics/crash data (device model, OS/app version,
        anonymized error codes, event counters such as lesson_play,
        lesson_complete, purchase_success); (f) Notification tokens and
        preferences for push messaging; and (g) Support communications if you
        email us. We do not receive payment card numbers.
      </p>

      <div className={s.title}>
        6) Sources of Data and Voluntary Provision
      </div>
      <p>
        Most information originates from you as you enroll in courses, play
        lessons, attempt quizzes, or manage downloads. Some data is derived
        automatically (e.g., completion flags when you reach the end of a
        lesson). Technical diagnostics arise from SDKs to keep the App stable.
        If you prefer not to provide Personal Data, you can use guest mode;
        however, cross-device sync and receipt restoration require an account
        and remote processing. You can disable notifications in OS settings at
        any time.
      </p>

      <div className={s.title}>7) Accounts, Guest Mode, and Sync</div>
      <p>
        You may access portions of the App without signing in. In guest mode,
        Progress Data and downloads are stored locally and may be lost if you
        delete the App or reset your device. Creating an account enables
        restoration across devices and secure entitlement checks. When signed
        in, we store limited, structured records in Firestore (e.g.,
        lastLessonId, completedLessons[]) keyed to your user ID, and we fetch
        only what is necessary to render the current view or resume playback.
      </p>

      <div className={s.title}>
        8) Courses, Lessons, and Video Playback
      </div>
      <p>
        Lesson playback typically uses HLS streaming hosted in cloud
        storage/CDN. To protect content, the App may use short-lived signed
        URLs, watermark overlays, or basic screen-record detection where
        platforms support it. Video frames are decoded on your device and not
        uploaded to our servers. We store lightweight playback states (e.g.,
        last position) to improve your experience. Subtitles/captions (if
        available) may be fetched on demand. We do not collect biometric data or
        microphone/camera input as part of lessons.
      </p>

      <div className={s.title}>9) Offline Downloads and Local Storage</div>
      <p>
        For selected lessons and materials, you can download for offline access.
        Downloaded segments and files are stored inside the app sandbox; we may
        encrypt them at rest using a key stored in the system keystore
        (Keychain/Keystore). If your subscription lapses or you sign out, the
        App may revoke access by rotating keys or invalidating manifests.
        Offline media remains on your device until you delete it or uninstall
        the App. We do not access your personal photos, contacts, or other
        device files.
      </p>

      <div className={s.title}>10) Quizzes and Assessments</div>
      <p>
        Quizzes measure understanding of covered topics. We store question IDs,
        your selected answers, and computed scores needed to present results and
        unlock subsequent lessons where applicable. Detailed answers are used to
        grade and to show feedback; we do not sell or share this data for
        advertising. Aggregate, de-identified performance statistics may be used
        to improve course quality. Quiz content is educational and must not be
        considered financial advice or recommendations to trade.
      </p>

      <div className={s.title}>
        11) Subscriptions, Trials, and Entitlements
      </div>
      <p>
        In-app purchases are handled by platform billing and mediated by
        RevenueCat. We receive limited Entitlement Data—product identifiers,
        trial eligibility, renewal success, expiration dates, cancellations—to
        grant or revoke premium access. We do not receive card details. You can
        manage or cancel subscriptions in your device account settings; refunds,
        grace periods, and taxes follow platform policies. We periodically
        recheck entitlements; an internet connection may be required to refresh
        access after long offline periods.
      </p>

      <div className={s.title}>12) Payments, Taxes, and Receipts</div>
      <p>
        Payments are processed by Apple or Google according to your region’s tax
        rules and currency. RevenueCat helps us validate receipts and
        synchronize entitlement status across devices. We retain transaction
        metadata only as needed to comply with accounting, fraud prevention, and
        legal obligations. For refunds or billing issues, use your platform’s
        purchase history; we will assist with entitlement state after the
        platform completes any adjustment.
      </p>

      <div className={s.title}>13) Notifications and In-App Messages</div>
      <p>
        With your permission, we may send push notifications about new modules,
        reminders to continue lessons, trial or subscription status, and
        relevant product updates. Delivery requires a device token used only to
        route messages; we avoid including sensitive content in notification
        bodies. You can disable notifications in OS settings and, where
        available, in-app toggles. Local reminders may occur even without
        network connectivity, subject to OS scheduling limits.
      </p>

      <div className={s.title}>14) Analytics and Crash Diagnostics</div>
      <p>
        We use Firebase Analytics and Crashlytics to understand reliability and
        feature adoption and to improve the user experience. Typical events:
        app_open, course_opened, lesson_play (with position), lesson_complete,
        materials_open, quiz_start/finish, paywall_shown, trial_started,
        purchase_success, download_start/complete/fail. Where feasible,
        analytics are aggregated or pseudonymized and exclude raw video content
        and personal text inputs. Platform privacy settings may limit analytics,
        which can reduce our ability to troubleshoot device-specific issues.
      </p>

      <div className={s.title}>15) Security Measures</div>
      <p>
        We implement layered safeguards appropriate to the risks: HTTPS/TLS for
        all communications; access rules in Firestore and Storage;
        least-privilege access to operational tools; environment separation;
        credential rotation and monitoring for misuse. Offline files may be
        encrypted, and signed URLs for streaming help restrict link reuse. No
        system is perfectly secure; we investigate incidents, notify when
        required by law, and take remedial actions to protect users and content.
      </p>

      <div className={s.title}>16) Data Retention and Deletion</div>
      <p>
        Local data and downloads remain until you delete them or uninstall the
        App. Cloud records (e.g., Progress Data, Entitlement Data references)
        are retained while your account remains active and as needed for
        operations and legal requirements. Crash and analytics logs are kept for
        operational windows to analyze trends and then de-identified or deleted.
        You may request deletion of account-linked records we control; after
        verification, we will delete or de-identify data unless retention is
        required by law (e.g., tax records of transactions).
      </p>

      <div className={s.title}>17) International Data Transfers</div>
      <p>
        Service providers may process data in jurisdictions outside your home
        region. Where required, we rely on appropriate safeguards (contractual
        clauses, platform mechanisms) to protect Personal Data during
        cross-border transfers. Regardless of location, providers must implement
        security measures consistent with this Policy and process data only on
        our instructions. You can contact us for high-level information on
        transfer mechanisms without revealing confidential security details.
      </p>

      <div className={s.title}>18) Your Rights and Choices</div>
      <p>
        Subject to law, you may have rights to access, rectify, delete, or port
        Personal Data; to restrict or object to certain processing; and to
        withdraw consent where processing relies on consent (e.g., notifications
        in some regions). In-app, you can delete downloads, clear progress for a
        course, sign out, or uninstall the App. Subscription management occurs
        in platform settings. To exercise legal rights, email
        alexwadek@gmail.com; we will verify identity and respond within a
        reasonable time. If we cannot fulfill a request due to legal or
        technical limits, we will explain unless prohibited.
      </p>

      <div className={s.title}>19) Children’s Privacy</div>
      <p>
        TraderLab is intended for adults interested in general trading education
        and is not directed to children. We do not knowingly collect Personal
        Data from users under the age where parental consent is required by law.
        If you believe a minor used the App without appropriate permission,
        contact alexwadek@gmail.com so we can remove information and restrict
        access as appropriate. Device-level parental controls can restrict
        purchases and notifications.
      </p>

      <div className={s.title}>20) Educational Disclaimer (No Financial Advice)</div>
      <p>
        All content in TraderLab is for education and informational purposes
        only. Nothing in the App constitutes financial, investment, or trading
        advice, and no outcome or profit is promised. Markets involve risk,
        including the risk of loss. You are solely responsible for your
        decisions and should seek guidance from qualified professionals before
        making financial choices. This disclaimer concerns content expectations
        and does not limit your applicable privacy rights.
      </p>

      <div className={s.title}>21) What We Do Not Collect or Sell</div>
      <p>
        We do not sell Personal Data. We do not collect payment card numbers,
        precise GPS location, your phone contacts, or biometric identifiers to
        deliver core features. We do not embed third-party advertising SDKs that
        profile users across apps. We do not upload your downloaded files to our
        servers. We do not disclose quiz responses to ad networks. Any marketing
        we run is contextual to our own product experiences.
      </p>

      <div className={s.title}>
        22) Third-Party Providers and Disclosures
      </div>
      <p>
        We rely on vetted providers acting on our instructions: Firebase (Auth,
        Firestore, Storage, Remote Config, Analytics, Crashlytics, Messaging),
        RevenueCat (subscriptions), CDN/hosting for video delivery, and email
        infrastructure for support. Providers are bound by terms requiring
        appropriate security and prohibiting use of Personal Data for
        independent marketing. We may disclose information when legally required
        by competent authorities after validating scope and jurisdiction, or in
        the context of a business transition where a successor honors this
        Policy or provides clear notice of changes and choices.
      </p>

      <div className={s.title}>
        23) Platform Privacy Labels and Data Safety
      </div>
      <p>
        We align App Store “Privacy Nutrition Labels” and Google Play “Data
        Safety” with the processing described here. Labels focus on categories
        (e.g., identifiers, usage data, diagnostics) and whether data is linked
        to you or used for tracking. If our processing changes—such as adding
        optional features (e.g., community, comments)—we will update both the
        in-app disclosures and the store labels before activation and, where
        required, seek consent.
      </p>

      <div className={s.title}>
        24) Cookies, Identifiers, and Do Not Track
      </div>
      <p>
        As a native app, TraderLab does not use browser cookies. SDKs may store
        identifiers necessary for authentication, entitlements, analytics, crash
        diagnostics, downloads coordination, and notifications. These
        identifiers act similarly to cookies for session continuity and
        measurement but are scoped to our services. The App does not rely on
        advertising identifiers for targeted advertising. Browser “Do Not Track”
        signals generally do not apply to native apps.
      </p>

      <div className={s.title}>
        25) Accuracy, Reliability, and Limitations
      </div>
      <p>
        We aim to provide accurate course metadata, lesson durations, and
        progress states; however, network conditions, OS restrictions, or
        provider outages can affect streaming and downloads. Offline access may
        be limited by storage space or system policies. Analytics events may be
        delayed or deduplicated by the platform. We recommend keeping the App
        updated and verifying critical details (e.g., course availability)
        in-app before relying on them for time-sensitive learning plans.
      </p>

      <div className={s.title}>
        26) Fair Use, Abuse Prevention, and Content Protection
      </div>
      <p>
        To maintain reliability, we may apply fair-use thresholds to downloads
        or unusually high streaming request rates. We may revoke expired signed
        URLs, rotate decryption keys on sign-out or entitlement loss, and limit
        concurrent device sessions per account according to platform rules. We
        do not install rootkit-style DRM; rather, we use proportionate measures
        to deter casual misuse while respecting user experience and privacy.
      </p>

      <div className={s.title}>
        27) Retention of Support Communications
      </div>
      <p>
        If you contact us by email, we process your message, contact details,
        and any attachments solely to address your request. We keep this
        correspondence for a reasonable period to improve support quality and to
        comply with legal obligations. Do not include sensitive financial
        information in support emails. You can request deletion of past threads;
        we will verify identity and, unless retention is legally required,
        delete relevant messages.
      </p>

      <div className={s.title}>28) Changes to This Policy</div>
      <p>
        We may update this Policy to reflect operational, legal, or regulatory
        developments. The “Last Updated” date will change when updates occur. If
        a change materially affects your rights or our processing, we will
        highlight the update in-app and, where required, seek consent before
        applying the change. Continued use after changes take effect signifies
        acceptance. If you disagree, stop using the App and request deletion of
        account-linked records we control.
      </p>

      <div className={s.title}>
        29) How to Exercise Your Rights (Process and Verification)
      </div>
      <p>
        To make a privacy request, email alexwadek@gmail.com and specify the
        request type (access, deletion, correction, restriction, objection,
        portability). Include helpful context such as device model, OS version,
        app version, and whether you use a signed-in account or guest mode. We
        will verify identity (for example, by a confirmation link or email
        reply) to prevent unauthorized access or deletion. Where feasible, we
        provide responses in a portable, machine-readable format and complete
        requests within a reasonable period consistent with applicable law.
      </p>
    </PolicyLayout>
  );
}
